import axios from 'axios'
import * as Sentry from '@sentry/react'
import { API_BASE_URL, X_API_KEY } from './api'

const fetchEvents = async (): Promise<any[]> => {
  const FULL_API_URL = `${API_BASE_URL}/all/full`

  try {
    const response = await fetch(FULL_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchEvents',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchEvents',
      },
    })
    throw error
  }
}

const deleteMyEvent = async (
  announceId: number | string,
  token: string,
): Promise<void> => {
  const DELETE_API_URL = `${API_BASE_URL}/user/${announceId}/event`

  try {
    const response = await axios.delete(DELETE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })

    if (response.status === 200) {
      localStorage.removeItem('myEvent')
    } else {
      throw new Error('Failed to delete event')
    }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'deleteMyEvent',
        announceId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'deleteMyEvent',
      },
    })
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error
    } else {
      throw error
    }
  }
}

const fetchGalleryByUserId = async (userId: number): Promise<string> => {
  const GALLERY_API_URL = `${API_BASE_URL}/${userId}/gallery`

  try {
    const response = await fetch(GALLERY_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch gallery')
    }
    const galleryData = await response.json()
    return `path/to/your/images/${galleryData.fileName}`
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchGalleryByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchGalleryByUserId',
      },
    })
    throw error
  }
}

const fetchTimings = async (): Promise<string[]> => {
  const ANNOUNCES_API_URL = `${API_BASE_URL}/events`

  try {
    const response = await fetch(ANNOUNCES_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch announces')
    }
    const announcesData = await response.json()
    const timings = announcesData.flatMap((Event: any) => Event.timing)
    return Array.from(new Set(timings))
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchTimings',
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchTimings',
      },
    })
    throw error
  }
}

const postMyEvent = async (
  myEvent: any,
  token: string,
  userId: string,
): Promise<any> => {
  const POST_API_URL = `${API_BASE_URL}/user/${userId}/event`

  const requestBody = {
    ...myEvent,
    userId: userId,
    smsOnly: myEvent.smsOnly || false,
    callOnly: myEvent.callOnly || false,
    whatsapp: myEvent.whatsapp || false,
    emailOnly: myEvent.emailOnly || false,
  }

  try {
    const response = await axios.post(POST_API_URL, requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 500) {
        throw new Error('server_error')
      }

      Sentry.captureException(error, {
        extra: {
          action: 'postMyEvent',
          userId,
          requestBody,
          statusCode: error.response?.status,
          errorData: error.response?.data,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'postMyEvent',
          statusCode: error.response?.status?.toString(),
        },
      })

      throw new Error(error.response?.data?.message || 'failed_to_post_event')
    }

    Sentry.captureException(error, {
      extra: {
        action: 'postMyEvent',
        userId,
        requestBody,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'non_axios_error',
        method: 'postMyEvent',
      },
    })
    throw new Error('unexpected_error')
  }
}

const fetchEventByUserId = async (userId: string, token: string) => {
  const url = `${API_BASE_URL}/user/${userId}/event`

  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })

    return response.data.event
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 500) {
        return null
      }
      if (error.response?.status === 404) {
        return null
      }
      if (error.response?.status === 401) {
        throw new Error('unauthorized')
      }

      Sentry.captureException(error, {
        extra: {
          action: 'fetchEventByUserId',
          userId,
          statusCode: error.response?.status,
          errorData: error.response?.data,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'api_error',
          method: 'fetchEventByUserId',
          statusCode: error.response?.status?.toString(),
        },
      })

      return null
    }

    Sentry.captureException(error, {
      extra: {
        action: 'fetchEventByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'non_axios_error',
        method: 'fetchEventByUserId',
      },
    })
    return null
  }
}

const updateLocalStorageWithUserEvent = (userAnnounces: any) => {
  if (
    !userAnnounces ||
    (Array.isArray(userAnnounces) && userAnnounces.length === 0)
  ) {
    localStorage.setItem('myEvent', JSON.stringify({}))
  } else {
    localStorage.setItem('myEvent', JSON.stringify(userAnnounces))
  }
}

const getUserEvent = async (userId: string, token: string): Promise<any> => {
  const USER_ANNOUNCE_API_URL = `${API_BASE_URL}/user/${userId}/event`

  try {
    const response = await fetch(USER_ANNOUNCE_API_URL, {
      headers: {
        Authorization: `Bearer ${token}`,
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch user events')
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'getUserEvent',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'getUserEvent',
      },
    })
    throw error
  }
}

const updateMyEvent = async (formData: any, token: string, userId: string) => {
  if (typeof userId !== 'string' || userId.trim() === '') {
    throw new Error('Invalid userId')
  }

  const url = `${API_BASE_URL}/user/${userId}/event`
  const announceData = formData

  try {
    const response = await axios.patch(url, announceData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-api-key': X_API_KEY,
      },
    })
    return response.data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'updateMyEvent',
        userId,
        announceData,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'updateMyEvent',
      },
    })
    if (axios.isAxiosError(error)) {
      throw error.response?.data || error
    } else {
      throw error
    }
  }
}

const fetchEventDetails = async (userId: string): Promise<any> => {
  const WORK_API_URL = `${API_BASE_URL}/events/${userId}`

  try {
    const response = await fetch(WORK_API_URL, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch event with id ${userId}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchEventDetails',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchEventDetails',
      },
    })
    throw error
  }
}

const fetchEventDetailsByUserId = async (userId: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/events?userId=${userId}`, {
      headers: {
        'x-api-key': X_API_KEY,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to fetch events details')
    }
    return response.json()
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        action: 'fetchEventDetailsByUserId',
        userId,
        timestamp: new Date().toISOString(),
      },
      tags: {
        type: 'api_error',
        method: 'fetchEventDetailsByUserId',
      },
    })
    throw error
  }
}

export {
  fetchEvents,
  updateMyEvent,
  fetchEventByUserId,
  fetchEventDetails,
  fetchEventDetailsByUserId,
  fetchGalleryByUserId,
  fetchTimings,
  deleteMyEvent,
  postMyEvent,
  updateLocalStorageWithUserEvent,
  getUserEvent,
}
